import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {DisplayOptions} from "app/modules/shared/interfaces/display-options";
import {IChartTitleData, ILargeCardInteriorOptions, SmallCardData} from "app/modules/shared/interfaces/card-data";
import {ICardInfo} from "app/modules/my-limegear/components/standalone-components/interfaces/stanalone-components.data";
import {NoDataActionType, NoDataSourceType} from "app/modules/shared/interfaces/no-data";
import {ReusableFunctions} from "app/modules/shared/classes/reusable-functions";
import {
    CommonStackAndBarChartOptions,
    ICommonChartParameters
} from "app/modules/shared/classes/common-stack-and-bar-chart-options";
import * as Highcharts from "highcharts";
import {UserAccessService} from "app/modules/shared/services/user-access.service";
import {NavigationHelpService} from "app/modules/shared/services/navigation-help.service";
import {ProductionCharts} from "app/modules/shared/classes/production-chart-options";
import {Router} from "@angular/router";

@Injectable({
    providedIn: 'root',
})
export class ECloseService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private _url = (this.serverUrl + '/api/v{version}/eclose').replace('{version}', this.version);

    constructor(
        private _http: HttpClient,
        private _router: Router,
        private _reusableFunctions: ReusableFunctions,
        private _userAccessService: UserAccessService,
        private _navigationHelpService: NavigationHelpService,
    ) {}

    getECloseData(parameters: IECloseParameters): Observable<IECloseData[]> {
        return this._http.post<any>(this._url + '/GetECloseData', parameters, {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        });
    }
    
    getClientECloseOptions(): Observable<IClientECloseOptionData[]> {
        return this._http.post<IClientECloseOptionData[]>(this._url + '/GetClientEcloseOptions', {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        });
    }

    updateClientECloseOptions(parameters: IClientECloseOptionData[]): Observable<number[]> {
        let data = {
            data: parameters,
        };

        return this._http.post<number[]>(this._url + '/UpdateClientECloseOptions', data, {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        });
    }

    setECloseParameters(displayOptions: DisplayOptions[], hFilterJSON: string): IECloseParameters {
        let channel = displayOptions.find(
            (item) => item.optionKey === 'channel'
        ).selectedOptions;
        let loanType = displayOptions.find(
            (item) => item.optionKey === 'loanCategory'
        ).selectedOptions;
        let loanPurpose = displayOptions.find(
            (item) => item.optionKey === 'loanPurpose'
        ).selectedOptions;
        let fromDatePeriod = displayOptions.find(
            (item) => item.optionKey === 'dateRange_y_q_m'
        ).selectedOptions;

        return {
            fromDatePeriod: fromDatePeriod ?? '',
            channel: channel ?? 'All',
            loanPurpose: loanPurpose ?? 'All',
            loanType: loanType ?? 'All',
            hFilterJSON: hFilterJSON ?? '[]',
            setRoiJson: '1',
            cardId: '',            
        };
    }

    initializeECloseSmallCard(
        cardInfo: ICardInfo,
        action: NoDataActionType
    ): SmallCardData {
        return {
            title: cardInfo.title,
            mainValue: '',
            unit: '',
            faIcon: '',
            faIconColor: '',
            comparisonValue: '',
            urlSegment: cardInfo.drillDUrlSegment,
            routerLink: '',
            svgData: '',
            isDataReady: false,
            hasData: false,
            searchTerm: '',
            action,
            noDataSource: NoDataSourceType.verySmallDataCard,
            showCardControllers: true,
            cardId: cardInfo.cardId,
        };
    }

    initializeECloseCard(cardInfo: IECloseCardInfo, action: NoDataActionType) {
        cardInfo.routerPath = '/reporting/production';
        let card: ILargeCardInteriorOptions = {
            action: action,
            componentTitle: cardInfo.title,
            chartTitleData: {} as IChartTitleData,
            searchTerm: '',
            hasData: false,
            isDataReady: false,
            showTitle: true,
            accessToUrl: cardInfo.routerPath + '/' + cardInfo.uniqueId,
            uniqueId: cardInfo.cardId,
            heightPercent: 99,
            minHeight: 300,
        };

        card.chartTitleData = {
            showChartCustomTitle: true,
            chartTile: cardInfo.title,
            showDashboardTextRight: false,
            showMainValue: false,
            chartMainValue: '',
            mainValueAlignTo: 'left',
            descriptionText: '',
            showDescriptionText: false,
            showMainValueToRight: cardInfo.showMainValueToRight,
        };
        
        cardInfo.largeCardInteriorOptions = Object.assign({}, card);
    }

    updateCard(
        cardInfo: IECloseCardInfo,
        displayOptions: DisplayOptions[],
        hFilterJSON: string,
        action: NoDataActionType,        
    ) {        
        let parameters = this.setECloseParameters(
            displayOptions, 
            hFilterJSON,            
        );
        
        parameters.cardId = cardInfo.cardId;
        if (cardInfo.setRoiJson) {
            parameters.setRoiJson = cardInfo.setRoiJson;
        }
        
        if (cardInfo.type === 'small-card') {
            this.updateSmallCardInteriorOptions(cardInfo, false, false, action);
        }
        else {
            this.updateLargeCardInteriorOptions(cardInfo, false, false, action);
        }
        
        this.getECloseData(parameters).subscribe(
            (res) => {
                if (cardInfo.type === 'small-card') {
                    this.setSmallCardData(cardInfo, res, action);
                }
                else {
                    this.setCardData(cardInfo, res, action, parameters);
                } 
            },
            (error) => {
                if (cardInfo.type === 'small-card') {
                    this.updateSmallCardInteriorOptions(cardInfo, false, true, NoDataActionType.error);
                }
                else {
                    this.updateLargeCardInteriorOptions(cardInfo, false, true, NoDataActionType.error);
                }               
            }
        );
    }

    setSmallCardData(
        cardInfo: ICardInfo,
        data: IECloseData[],
        action: NoDataActionType,
    )
    {
        let eCloseVolumePercentageData = this.prepareECloseVolumePercentageData(data);
        let eCloseSmallCard = cardInfo.card;            
        let hasData = data && data.length > 0;
        
        if (hasData)  {
            let isCardUnits = cardInfo.dataType === 'number';
            let queryParams = {
                titleSuffix: `eClose Loans Funded`,
                eCloseOptionKey: 0,
                excludeTraditional: 1,
            };

            eCloseSmallCard.urlSegment = cardInfo.urlSegment;
            eCloseSmallCard.queryParams = queryParams;
            eCloseSmallCard.mainValue = isCardUnits
                ? eCloseVolumePercentageData.eCloseFundings.toString()
                : this._reusableFunctions.formatNumber(
                    eCloseVolumePercentageData.eCloseVolume, 0, 'currency'
                );
            eCloseSmallCard.bottomCardData = this.getBottomCardData(
                isCardUnits,
                eCloseVolumePercentageData.eClosePercent,
                eCloseVolumePercentageData.eCloseVolumePercent
            );                
        }
        eCloseSmallCard.hasData = hasData;
        eCloseSmallCard.isDataReady = true;
        eCloseSmallCard.action = action;
        cardInfo.card = Object.assign({}, eCloseSmallCard);
    }

    getBottomCardData(
        isCardUnits: boolean,
        eClosePercent: number,
        eCloseVolumePercent: number,
        
        ) {
        const description = 'of total funded';
        let value = isCardUnits
            ? `${eClosePercent}%`
            : `${eCloseVolumePercent}%`;
        value = isNaN(eClosePercent) ? '0%' : value;
        return [{ value, description, hasAccess: true }];
    }

    prepareECloseVolumePercentageData(data: IECloseData[]): IECloseVolumePercentageDataInfo {
        const eCloseData = data.filter((closings) => closings.category !== 'Traditional');
        const totalUnits = data.reduce((a, { nbr }) => a + nbr, 0);
        const totalVolume = data.reduce((a, { amount }) => a + amount, 0);
        const eCloseUnits = eCloseData.reduce((a, { nbr }) => a + nbr, 0);
        const eCloseVolume = eCloseData.reduce((a, { amount }) => a + amount, 0);
        return {
            eCloseFundings: eCloseUnits,
            eClosePercent: Math.round((100 * eCloseUnits) / totalUnits),
            eCloseVolume,
            eCloseVolumePercent: Math.round((100 * eCloseVolume) / totalVolume),
            totalUnits,
            totalVolume,
        };
    }

    updateLargeCardInteriorOptions(
        cardInfo: ICardInfo,
        hasData: boolean,
        isDataReady: boolean,
        action: NoDataActionType
    ) {
        let cardInteriorOptions = cardInfo.largeCardInteriorOptions;
        cardInteriorOptions.hasData = hasData;
        cardInteriorOptions.isDataReady = isDataReady;
        cardInteriorOptions.action = action;
        cardInfo.largeCardInteriorOptions = Object.assign({}, cardInteriorOptions);
    }

    updateSmallCardInteriorOptions(
        cardInfo: ICardInfo,
        hasData: boolean,
        isDataReady: boolean,
        action: NoDataActionType
    ) {
        let card = cardInfo.card;
        card.hasData = hasData;
        card.isDataReady = isDataReady;
        card.action = action;
        cardInfo.card = Object.assign({}, card);
    }

    setCardData(
        cardInfo: IECloseCardInfo,
        data: IECloseData[],
        action: NoDataActionType,
        parameters: IECloseParameters,
    ) {
        let hasData = data && data.length > 0;
        if (hasData) {            
            let largeCard = cardInfo.largeCardInteriorOptions;    
            try {
                if (cardInfo.cardId === 'insights_estimatedECloseSaving') {
                    let estimatedSavingTotal =
                        this._reusableFunctions.calculateSumByProperty(
                            data,
                            cardInfo.yProperty
                        );

                    largeCard.chartTitleData.chartMainValue =
                        this._reusableFunctions.formatNumber(
                            estimatedSavingTotal,
                            0,
                            'currency',
                            true
                        );
                } else if (cardInfo.cardId === 'insights_averageCostSavingPerLoan') {
                    data = data.filter((x) => x.category === 'eClose Only');
                    let groupingNames = this._reusableFunctions.getUniqueItemList(
                        data,
                        cardInfo.seriesProperty
                    );

                    groupingNames.forEach((gpName) => {
                        let totalMonthlyRev = 0;
                        let totalLoans = 0;

                        let groupData = data.filter((row) => row.category === gpName);
                        groupData.forEach((dat) => {
                            totalMonthlyRev += dat.perPeriodAvgRevenuePerLoan * dat.nbr;
                            totalLoans += dat.nbr;
                        });

                        let avgPerLoan = totalLoans > 0 ? totalMonthlyRev / totalLoans : 0;
                        let avgFormatted = this._reusableFunctions.formatNumber(
                            avgPerLoan,
                            0,
                            'currency'
                        );

                        if (gpName.toLowerCase() === 'eClose Only'.toLowerCase()) {
                            largeCard.chartTitleData.chartMainValue = avgFormatted;
                            largeCard.chartTitleData.mainValueUnit = 'eClose avg';
                            largeCard.chartTitleData.isMainValueUnitVisible = true;
                        }
                    });
                }

                largeCard.chartTitleData.showMainValue = cardInfo.showTotalAt != null;
            } catch (e) {
                console.log('An error in title summary area');
            }
            this.setChartData(data, cardInfo, parameters);
            largeCard.showCardControlArea = true;
            largeCard.hasData = hasData;
            largeCard.isDataReady = true;
            largeCard.action = action;
            cardInfo.largeCardInteriorOptions = Object.assign({}, largeCard);
        }  
        else {
            this.updateLargeCardInteriorOptions(cardInfo, false, true, action);
        }
    }

    setChartData(
        data: IECloseData[], 
        cardInfo: IECloseCardInfo,
        parameters: IECloseParameters
    ) {
        let processedData = this.setECloseSavingChartData(data, cardInfo);
        let adjustCardHeightPerPageToMatch = false;
        let url = this._router.url.split('?')[0];
        if (url === '/production/insights/eclose-roi-trends'
            || url === '/production/insights/eclose-adoption-trends') {
            cardInfo.height = 270;
            adjustCardHeightPerPageToMatch = true;
        }

        let reportTitlePrefix = '';
        
        if (cardInfo.setReportTitle) {
            reportTitlePrefix = cardInfo.title;
            cardInfo.largeCardInteriorOptions.queryParams = {
                titleSuffix: cardInfo.title,
            };
        }

        let chartOptionParams: ICommonChartParameters = {
            reportTitle: reportTitlePrefix, 
            secondaryTitle: '',
            xAxisText: cardInfo.xTitle != null ? data[0].viewBy : null,
            yAxisText: cardInfo.yTitle,
            initialSeriesData: processedData,
            showLegend: cardInfo.showLegend,
            legendAlign: 'left',
            route: cardInfo.routerPath,
            urlSegment: cardInfo.uniqueId,
            purpose: parameters.loanPurpose,
            loanType: parameters.loanType,
            height: cardInfo.height,
            spacingBottom: 10,
            showDataLabels: cardInfo.showDataLabels,
            isSackChart: cardInfo.isSackChart,
            tooltipitleSuffix: cardInfo.tooltipTitleSuffix,
            showTooltipSummary: cardInfo.cardId!== 'insights_averageCostSavingPerLoan',
        };

        let eCloseROICharts = new CommonStackAndBarChartOptions(
            this._reusableFunctions,
            this._userAccessService,
            this._navigationHelpService
        );

        let chartOptions = eCloseROICharts.createNewStackedChart(chartOptionParams);
        setTimeout(() => {
            cardInfo.largeCardInteriorOptions.chart = new Highcharts.Chart(cardInfo.cardId, chartOptions);
            if (cardInfo.setCardHeight && cardInfo.cardHeightRatio && adjustCardHeightPerPageToMatch) {
                let htmlElement = document.getElementById(cardInfo.cardId);
                if (htmlElement?.parentElement?.clientHeight) { 
                    let height = htmlElement.parentElement.clientHeight * cardInfo.cardHeightRatio;
                    cardInfo.largeCardInteriorOptions.chart.setSize(null, height);
                }
            }
        }, 0);
    }

    setECloseSavingChartData(stackData: IECloseData[], cardInfo: IECloseCardInfo) {
        let dataSet = stackData.map((obj) => ({ ...obj }));
        let stackColumnsData: ProductionCharts.ChartdataSeries[] = [];
        let groupingNames = this._reusableFunctions.getUniqueItemList(
            dataSet,
            cardInfo.seriesProperty
        );
        if (cardInfo.chartType === 'column') {
            groupingNames.forEach((ItemClass) => {
                let classData = dataSet.filter(
                    (item) => item[cardInfo.seriesProperty] === ItemClass
                );
                let processedData = this.setPointData(classData, cardInfo);
                let columnIndex = null;
                let pointPlacement = null;
                let borderWidth = null;
                if (cardInfo.cardId === 'insights_averageCostSavingPerLoan') {
                    let isAllLoans = ItemClass.toLowerCase() === 'All Loans'.toLowerCase();
                    columnIndex = isAllLoans ? 1 : 0;
                    pointPlacement = isAllLoans ? -0.25 : 0;
                    borderWidth = 0;
                }

                stackColumnsData.push({
                    name: ItemClass,
                    data: processedData,
                    type: cardInfo.chartType,
                    color: processedData[0]?.color,
                    pointPlacement: pointPlacement,
                    index: columnIndex,
                    borderWidth: borderWidth,
                });
            });
        } else {
            let dataSetNonStacked: IECloseData[] = [];
            groupingNames.forEach((prop) => {
                let dataSetByProperty = dataSet.filter(
                    (item) => item[cardInfo.seriesProperty] === prop
                );
                let sum = this._reusableFunctions.calculateSumByProperty(
                    dataSetByProperty,
                    cardInfo.yProperty
                );
                let sumUnits = this._reusableFunctions.calculateSumByProperty(
                    dataSetByProperty,
                    'nbr'
                );
                if (dataSetByProperty?.length > 0) {
                    let chartDataObject = dataSetByProperty[0];
                    chartDataObject[cardInfo.yProperty] = sum;
                    chartDataObject['nbr'] = sumUnits;
                    dataSetNonStacked.push(chartDataObject);
                }
            });

            let pointDataInf = this.setPointData(dataSetNonStacked, cardInfo);
            stackColumnsData.push({
                name: 'This Period',
                data: pointDataInf,
                type: cardInfo.chartType,
            });
        }
        return stackColumnsData;
    }

    setPointData(classData: IECloseData[], cardInfo: IECloseCardInfo) {
        let chartDataPoints: ProductionCharts.ChartDataPoint[] = [];
        let colors = this._reusableFunctions.getECloseColors();

        classData.forEach((item) => {
            chartDataPoints.push({
                y: item[cardInfo.yProperty],
                name: this.setXAxisLabel(item, cardInfo),
                color: colors[item.category],
                properties: {
                    datePeriodDescription: item.datePeriodDescription,
                    period:
                        cardInfo.cardId === 'insights_estimatedSavingByECloseMethod'
                            ? 0
                            : item.period,
                    units: item.nbr,
                    eCloseOptionKey: item.eCloseOptionKey,
                    titleSuffix: item.category,
                    dataLabel: this._reusableFunctions.formatNumber(
                        item[cardInfo.yProperty],
                        0,
                        'currency',
                        true
                    ),
                },
            });
        });
        return chartDataPoints;
    }

    setXAxisLabel(dataPoint: IECloseData, cardInfo: IECloseCardInfo) {
        let label = dataPoint[cardInfo.xProperty];
        if (cardInfo.formatMonthLabel && dataPoint.viewBy === 'Month' && label) {
            let labelItems = label.split('-');
            return labelItems[0] + "' " + labelItems[1].slice(2, 4);
        }
        return label;
    }
}

export interface IECloseParameters {
    fromDatePeriod: string;
    channel: string;
    loanType: string;
    loanPurpose: string;
    hFilterJSON: string;
    cardId: string;
    setRoiJson: '1' | '0';
}

export interface IECloseData {
    periodGroup: string;
    year: number;
    datePeriodDescription: string;
    period: number;
    milestoneKey: number;
    category: string;
    viewBy: string;
    nbr: number;
    amount: number;
    revenue: number;
    description?: string;
    loanRevenue?: number;
    perPeriodAvgRevenuePerLoan?: number;
    totalLoanAmount?: number;
    totalNumOfLoans?: number;
    totalRevenuePerLoan?: number;
    eCloseOptionKey: number;
    nbrOfLoans: number;
}

export interface IClientECloseOptionData {
    clientECloseOptionKey: number;
    clientKey: number;
    eCloseOptionKey: number;
    eCloseOption: string;
    roi: number;
}

export interface IECloseCardInfo extends ICardInfo{
    uniqueId: string;
    isSackChart?: boolean;
    chartType?: 'column' | 'bar';
    yProperty?: string;
    xProperty?: string;
    seriesProperty?: string;
    yTitle?: string;
    xTitle?: string;
    showLegend?: boolean;    
    showDataLabels?: boolean;
    tooltipTitleSuffix?: string;
    showTotalAt?: 'below-title' | 'with-title';
    formatMonthLabel?: boolean;
    showMainValueToRight?: boolean;
    setRoiJson?: '1' | '0';
    setReportTitle?: boolean;
}

interface IECloseVolumePercentageDataInfo {
    eCloseFundings: number;
    eClosePercent: number;
    eCloseVolume: number;
    eCloseVolumePercent: number;
    totalUnits: number;
    totalVolume: number;
}
